import * as Yup from "yup";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

/**
 * Shift slot length in minutes — same idea as Admin `RoleFields.validateTimeMargins`
 * (overnight shift when end clock is on or before start).
 */
export const getShiftSlotDurationMinutes = (shiftStart, shiftEnd) => {
  if (shiftStart == null || shiftEnd == null || shiftStart === "" || shiftEnd === "") {
    return null;
  }
  const formats = ["HH:mm", "H:mm", "hh:mm A", "h:mm A"];
  let startDate = dayjs(shiftStart, formats, true);
  let endDate = dayjs(shiftEnd, formats, true);
  if (!startDate.isValid()) startDate = dayjs(shiftStart);
  if (!endDate.isValid()) endDate = dayjs(shiftEnd);
  if (!startDate.isValid() || !endDate.isValid()) return null;
  if (endDate.isSameOrBefore(startDate)) {
    endDate = endDate.add(1, "day");
  }
  return endDate.diff(startDate, "minute");
};

/** Mirrors StepTwo validationSchema helpers — training vs shift window + event dates. */

export const dateTimeToClockMinutes = (value) => {
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.hour() * 60 + d.minute();
};

export const shiftStringToClockMinutes = (value) => {
  if (value == null || value === "") return null;
  const d = dayjs(value, ["HH:mm", "H:mm", "hh:mm A", "h:mm A"], true);
  if (!d.isValid()) return null;
  return d.hour() * 60 + d.minute();
};

export const trainingClockOverlapsShiftWindow = (
  trainingStart,
  trainingEnd,
  shiftStart,
  shiftEnd,
) => {
  const t0 = dateTimeToClockMinutes(trainingStart);
  const t1 = dateTimeToClockMinutes(trainingEnd);
  const s0 = shiftStringToClockMinutes(shiftStart);
  const s1 = shiftStringToClockMinutes(shiftEnd);
  if (t0 === null || t1 === null || s0 === null || s1 === null) return false;

  if (s1 > s0) {
    return t0 < s1 && t1 > s0;
  }

  const shiftStartTime = dayjs(
    shiftStart,
    ["HH:mm", "H:mm", "hh:mm A", "h:mm A"],
    true,
  );
  const shiftEndTime = dayjs(
    shiftEnd,
    ["HH:mm", "H:mm", "hh:mm A", "h:mm A"],
    true,
  );
  const trainingStartTime = dayjs(
    dayjs(trainingStart).format("HH:mm"),
    "HH:mm",
    true,
  );
  const trainingEndTime = dayjs(
    dayjs(trainingEnd).format("HH:mm"),
    "HH:mm",
    true,
  );
  if (
    !shiftStartTime.isValid() ||
    !shiftEndTime.isValid() ||
    !trainingStartTime.isValid() ||
    !trainingEndTime.isValid()
  ) {
    return false;
  }
  return (
    trainingStartTime.isBefore(shiftEndTime) &&
    trainingEndTime.isAfter(shiftStartTime)
  );
};

export const clockMinutesInsideShiftWindow = (mins, shiftStart, shiftEnd) => {
  const s0 = shiftStringToClockMinutes(shiftStart);
  const s1 = shiftStringToClockMinutes(shiftEnd);
  if (mins === null || s0 === null || s1 === null) return false;
  if (s1 <= s0) return mins >= s0 || mins < s1;
  return mins >= s0 && mins < s1;
};

const trainingConflictMessagePath = (shiftPath, field) =>
  `${shiftPath}.training_attributes.${field}`;

/**
 * @param {object} messages - resolved UI strings (same semantics as StepTwo message keys)
 * @param {string|undefined} eventStartAt
 * @param {string|undefined} eventEndAt
 */
export const createTrainingShiftConflictTest =
  (messages, eventStartAt, eventEndAt) =>
  function trainingConflictWithShift(shiftValue) {
    const training = shiftValue?.training_attributes;

    if (!training || training._destroy) return true;

    const shiftStart = shiftValue?.starts_at;
    const shiftEnd = shiftValue?.ends_at;

    const trainingStart = training?.starts_at;
    const trainingEnd = training?.ends_at;

    if (!shiftStart || !shiftEnd || (!trainingStart && !trainingEnd)) {
      return true;
    }

    if (!eventStartAt || !eventEndAt) return true;

    const eventStartDate = dayjs(eventStartAt).startOf("day");
    const eventEndDate = dayjs(eventEndAt).endOf("day");

    const isDateInEventRange = (date) =>
      date.isBefore(eventEndDate) &&
      date.isAfter(eventStartDate.subtract(1, "millisecond"));

    const isTimeInShiftWindow = (clockMinutes) =>
      clockMinutesInsideShiftWindow(clockMinutes, shiftStart, shiftEnd);

    const path = this.path;

    if (trainingStart && !trainingEnd) {
      const trainingStartDate = dayjs(trainingStart).startOf("day");
      if (!isDateInEventRange(trainingStartDate)) return true;

      if (
        !isTimeInShiftWindow(dateTimeToClockMinutes(trainingStart))
      ) {
        return true;
      }

      return this.createError({
        path: trainingConflictMessagePath(path, "starts_at"),
        message: messages.trainingConflictWithShift,
      });
    }

    if (!trainingStart && trainingEnd) {
      const trainingEndDate = dayjs(trainingEnd).startOf("day");
      if (!isDateInEventRange(trainingEndDate)) return true;

      if (!isTimeInShiftWindow(dateTimeToClockMinutes(trainingEnd))) {
        return true;
      }

      return this.createError({
        path: trainingConflictMessagePath(path, "ends_at"),
        message: messages.trainingConflictWithShift,
      });
    }

    const trainingStartDate = dayjs(trainingStart).startOf("day");
    const trainingEndDate = dayjs(trainingEnd).endOf("day");

    const datesOverlap =
      trainingStartDate.isBefore(eventEndDate) &&
      trainingEndDate.isAfter(eventStartDate);

    if (!datesOverlap) return true;

    if (
      !trainingClockOverlapsShiftWindow(
        trainingStart,
        trainingEnd,
        shiftStart,
        shiftEnd,
      )
    ) {
      return true;
    }

    const startDateInEvent =
      !trainingStartDate.isBefore(eventStartDate) &&
      !trainingStartDate.isAfter(eventEndDate);

    const endDateInEvent =
      !trainingEndDate.isBefore(eventStartDate) &&
      !trainingEndDate.isAfter(eventEndDate);

    const startInsideShift =
      startDateInEvent &&
      clockMinutesInsideShiftWindow(
        dateTimeToClockMinutes(trainingStart),
        shiftStart,
        shiftEnd,
      );

    const endInsideShift =
      endDateInEvent &&
      clockMinutesInsideShiftWindow(
        dateTimeToClockMinutes(trainingEnd),
        shiftStart,
        shiftEnd,
      );

    const conflictErrors = [];

    if (startInsideShift) {
      conflictErrors.push(
        new Yup.ValidationError(
          messages.trainingConflictWithShift,
          trainingStart,
          trainingConflictMessagePath(path, "starts_at"),
        ),
      );
    }

    if (endInsideShift) {
      conflictErrors.push(
        new Yup.ValidationError(
          messages.trainingConflictWithShift,
          trainingEnd,
          trainingConflictMessagePath(path, "ends_at"),
        ),
      );
    }

    if (conflictErrors.length === 0) {
      const endDateTimeExtendsPastEvent =
        eventEndAt && dayjs(trainingEnd).isAfter(dayjs(eventEndAt));
      const startDateBeforeEvent = trainingStartDate.isBefore(eventStartDate);

      if (endDateTimeExtendsPastEvent && !startDateBeforeEvent) {
        return true;
      }
      if (startDateBeforeEvent && !endDateTimeExtendsPastEvent) {
        return new Yup.ValidationError([
          new Yup.ValidationError(
            messages.trainingConflictWithShift,
            trainingStart,
            trainingConflictMessagePath(path, "starts_at"),
          ),
        ]);
      }
      return new Yup.ValidationError([
        new Yup.ValidationError(
          messages.trainingConflictWithShift,
          trainingStart,
          trainingConflictMessagePath(path, "starts_at"),
        ),
        new Yup.ValidationError(
          messages.trainingConflictWithShift,
          trainingEnd,
          trainingConflictMessagePath(path, "ends_at"),
        ),
      ]);
    }

    return new Yup.ValidationError(conflictErrors);
  };
